import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Inactivity timeout in milliseconds (20 minutes)
const INACTIVITY_TIMEOUT = 20 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const inactivityTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Logout function (defined early for use in inactivity handler)
  const logout = useCallback(async (showMessage = false) => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('token');
    localStorage.removeItem('lastActivity');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    
    if (showMessage) {
      // Store message to show on login page
      sessionStorage.setItem('logoutReason', 'You have been logged out due to inactivity.');
    }
  }, []);

  // Reset inactivity timer
  const resetInactivityTimer = useCallback(() => {
    if (!token || !user) return;
    
    lastActivityRef.current = Date.now();
    localStorage.setItem('lastActivity', lastActivityRef.current.toString());
    
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    
    inactivityTimerRef.current = setTimeout(() => {
      console.log('User inactive for 30 minutes, logging out...');
      logout(true);
      window.location.href = '/login';
    }, INACTIVITY_TIMEOUT);
  }, [token, user, logout]);

  // Check for inactivity on page load/focus
  const checkInactivity = useCallback(() => {
    if (!token) return;
    
    const lastActivity = parseInt(localStorage.getItem('lastActivity') || '0', 10);
    const now = Date.now();
    
    if (lastActivity && (now - lastActivity) > INACTIVITY_TIMEOUT) {
      console.log('Session expired due to inactivity');
      logout(true);
      window.location.href = '/login';
    }
  }, [token, logout]);

  // Set up activity listeners
  useEffect(() => {
    if (!token || !user) return;

    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      resetInactivityTimer();
    };

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Check inactivity on window focus
    const handleFocus = () => {
      checkInactivity();
      resetInactivityTimer();
    };
    window.addEventListener('focus', handleFocus);

    // Initial timer setup
    resetInactivityTimer();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      window.removeEventListener('focus', handleFocus);
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [token, user, resetInactivityTimer, checkInactivity]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      // Initialize last activity on successful auth
      localStorage.setItem('lastActivity', Date.now().toString());
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('lastActivity', Date.now().toString());
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    // Clear any logout reason message
    sessionStorage.removeItem('logoutReason');
    return userData;
  };

  const superLogin = async () => {
    const response = await axios.post(`${API}/auth/super-login`);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('lastActivity', Date.now().toString());
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const changePassword = async (currentPassword, newPassword, confirmPassword) => {
    await axios.post(`${API}/auth/change-password`, {
      current_password: currentPassword,
      new_password: newPassword,
      confirm_password: confirmPassword
    });
    await fetchUser();
  };

  const isAdmin = user?.user_type === 'admin' || user?.user_type === 'super_user';
  const isManager = user?.user_type === 'manager' || isAdmin;
  const isStaff = user?.user_type === 'staff';
  const isSuperUser = user?.user_type === 'super_user';

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      superLogin,
      logout,
      changePassword,
      isAdmin,
      isManager,
      isStaff,
      isSuperUser,
      fetchUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
